/**
 * useMicEngine:
 * 
 * A hook to manage microphone state, including permission handling,
 * activation/deactivation logic, and interaction with the MicrophoneImpl singleton.
 * In particular, it handles the delayed logic cutoff on deactivation to avoid
 * clipping the end of speech.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { MicrophoneImpl, KeyType, MIC_DEACTIVATION_DELAY_MS } from "../../utils/MicrophoneImpl";
import { useMicrophonePermissions } from "../MicrophonePermissions";
import {
  AudioMessage_ServerInfo_HealthStatus as HealthStatus,
} from "../../generated/audio_message";

const MIC_VISUAL_FADE_MS = 300;

export function useMicEngine(opts?: {
  healthStatus?: HealthStatus;                // inject external health
  onActivated?: () => void;                   // UI callback when mic goes active
  onDeactivatingLogic?: () => void;           // when logic cutoff happens
}) {
  const {
    healthStatus,
    onActivated,
    onDeactivatingLogic,
  } = opts || {};

  const [mic] = useState(() => MicrophoneImpl.getInstance());

  const [started, setStarted] = useState(false);
  const [active, setActive] = useState(false);
  const [midDeactivation, setMidDeactivation] = useState(false); // visual phase during fade
  const [hasEverTransmitted, setHasEverTransmitted] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [
    { needsPermission, showPermissionOverlay },
    { requestPermissionAndStart, setNeedsPermission }
  ] = useMicrophonePermissions();

  // internal flags/timers
  const isDeactivatingRef = useRef(false);
  const logicTimerRef = useRef<number | null>(null);
  const visualTimerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      // Clean timers on unmount
      if (logicTimerRef.current !== null) {
        clearTimeout(logicTimerRef.current);
        logicTimerRef.current = null;
      }
      if (visualTimerRef.current !== null) {
        clearTimeout(visualTimerRef.current);
        visualTimerRef.current = null;
      }
    };
  }, []);

  const activateMicrophone = useCallback(async (key: KeyType = KeyType.SPACE) => {
    // Permission gate
    if (needsPermission) {
      const granted = await requestPermissionAndStart();
      if (!granted) return;
      // UX: require a second tap to actually transmit
      return;
    }

    // Health gate
    if (healthStatus === HealthStatus?.SEVERELY_DEGRADED) {
      return;
    }

    // Cancel any pending deactivation
    if (logicTimerRef.current !== null) {
      clearTimeout(logicTimerRef.current);
      logicTimerRef.current = null;
    }
    if (visualTimerRef.current !== null) {
      clearTimeout(visualTimerRef.current);
      visualTimerRef.current = null;
    }

    // First start
    if (!started && mic.started === false) {
      try {
        setIsInitializing(true);
        await mic.start();
        setStarted(true);
        setIsInitializing(false);

        // If user deactivated while starting, bail out
        if (isDeactivatingRef.current) {
          isDeactivatingRef.current = false;
          return;
        }

        mic.setCallbackEnabled(true, key);
      } catch (e) {
        // Starting failed → bounce back to permission flow
        setNeedsPermission(true);
        setIsInitializing(false);
        return;
      }
    } else {
      // Subsequent activations just enable callback
      mic.setCallbackEnabled(true, key);
    }

    setHasEverTransmitted(true);
    setActive(true);
    setMidDeactivation(false);
    onActivated?.();
  }, [
    needsPermission,
    requestPermissionAndStart,
    setIsInitializing,
    setNeedsPermission,
    setHasEverTransmitted,
    started,
    healthStatus,
    onActivated,
  ]);

  const deactivateMicrophone = useCallback(() => {
    // If neither active nor initializing, ignore
    if (!active && !isInitializing) return;

    isDeactivatingRef.current = true;
    setMidDeactivation(true);     // start visual fade
    setActive(false);             // UI off now; actual logic cutoff happens after delay

    // Logic cutoff (stop sending audio) after delay
    logicTimerRef.current = window.setTimeout(() => {
      onDeactivatingLogic?.();
      mic.setCallbackEnabled(false);
      logicTimerRef.current = null;
    }, MIC_DEACTIVATION_DELAY_MS);

    // Visual fade completion
    visualTimerRef.current = window.setTimeout(() => {
      setMidDeactivation(false);
      visualTimerRef.current = null;
      isDeactivatingRef.current = false;
    }, MIC_VISUAL_FADE_MS);
  }, [active, isInitializing, onDeactivatingLogic]);

  const resetHasEverTransmitted = useCallback(() => {
    setHasEverTransmitted(false);
  }, []);

  return {
    mic,
    started,
    active,
    midDeactivation,
    needsPermission,
    isInitializing,
    activateMicrophone,
    deactivateMicrophone,
    hasEverTransmitted,
    showPermissionOverlay,
    resetHasEverTransmitted,
  };
}
